/**
 * 개인정보 가리기 조작 (티켓 #461·#459).
 *
 * 세 가지 얼굴을 가진다.
 * - **평소(안 가림)**: 「가리기」 — 눌러서 그 자리에서 가린다.
 * - **가려짐 + 암호 확인됨**: 「가리기 해제」 — 검색칸에 매장 암호를 쳐서 맞았을 때만 뜬다.
 * - **가려짐 + 암호 안 들어옴**: 아무것도 안 보인다 — 잠겨 있다는 티가 나면 안 되기 때문이다
 *   (점검 나온 사람 눈에는 그냥 정상 화면이어야 한다).
 *
 * 2026-08-28: 두 얼굴을 **자리별로 갈랐다**(`place`).
 * - `menu` = 왼쪽 메뉴 ⚙️ 설정 안 — 평소의 「가리기」를 여기 둔다. 상단 띠에 늘 떠 있던
 *   눈 아이콘이 "이게 왜 여기 있지" 싶게 뜬금없다는 지적을 받아 옮겼다. 급할 때 두 번 누르는
 *   비용은 있지만, 가리는 조작은 하루에 몇 번 있는 일이 아니다.
 * - `header` = 상단 띠 — **「가리기 해제」만** 남는다. 암호를 친 직후가 정확히 그 버튼이
 *   필요한 때이고, 지금 개인정보가 드러나 있다는 **경고 표시**를 겸하므로 눈에 보여야 한다.
 *   ⚠️ 이걸 메뉴 안으로 같이 옮기면 안 된다 — 풀린 줄 모르고 화면을 켜 두게 된다.
 */

import { usePrivacyMask } from "../../context/PrivacyMaskContext";

export default function PrivacyUnlockButton({
  className = "",
  place = "header",
}: {
  className?: string;
  /** header = '가리기 해제'만 / menu = '가리기'만 */
  place?: "header" | "menu";
}) {
  const { enabled, armed, unlockedByPassword, unmask, remask } = usePrivacyMask();

  // 가려져 있고 암호도 안 들어온 상태 = 아무 데도 안 보인다(잠근 티를 내지 않는다).
  if (enabled && !armed) return null;

  const unlocking = enabled && armed;

  if (place === "header") {
    // 띠에는 '지금 드러나 있다'는 경고 겸 되돌리기 버튼만 선다.
    if (!unlocking) return null;
    return (
      <button
        type="button"
        data-testid="privacy-mask-button"
        onClick={unmask}
        title="고객 개인정보를 원래대로 보여줍니다"
        className={`flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-brand-500 px-2.5 text-xs font-semibold text-brand-600 transition hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/[0.03] ${className}`}
      >
        가리기 해제
      </button>
    );
  }

  // 메뉴 자리 — 암호로 잠깐 연 세션이면 "다시" 덮는 것이고, 평소 상태면 그냥 켜는 것이다.
  if (unlocking) return null;
  return (
    <button
      type="button"
      data-testid="privacy-mask-button"
      onClick={remask}
      title="고객 개인정보를 화면에서 가립니다"
      className={className}
    >
      <span aria-hidden>🙈</span>
      {unlockedByPassword ? "개인정보 다시 가리기" : "개인정보 가리기"}
    </button>
  );
}
